/**
 * CL-STOCK-01 — the seller's reusable stock locations.
 *
 * The address book lives on the seller's own profile:
 *
 *   currentUser.attributes.profile.privateData.stockLocations = [ {…}, {…} ]
 *
 * Sharetribe returns privateData to the owning user and to nobody else, so the
 * browser can read and write it with the ordinary marketplace SDK. No server
 * endpoint is involved (N2).
 *
 * D3 — COPY, NOT LINK. Picking a location copies its address onto the listing.
 * From that moment the listing owns its copy. Editing, archiving or deleting a
 * saved location NEVER changes a listing, and nothing in this module (or anywhere
 * else in this feature) calls sdk.ownListings.update in response to such a change.
 *
 * Every function here is pure. `id` and `now` are injected by the caller so that
 * crypto.randomUUID() and the clock stay out of the tested code.
 */

import { types as sdkTypes } from '../sdkLoader';
import { SHIPPING_CONFIG_KEY, SHIPPING_ORIGIN_PRIVATE_KEY, buildPublicOrigin } from './shipping';
// The postcode-implies-street rule lives with the geocoder that produces the postcode,
// so the address book and the geocoder cannot disagree about what "street" means.
import { precisionFor } from './placeComponents';

const { LatLng } = sdkTypes;

export const STOCK_LOCATIONS_PRIVATE_KEY = 'stockLocations';
export const STOCK_LOCATION_ID_PUBLIC_KEY = 'stockLocationId';
export const STOCK_LOCATION_SCHEMA_VERSION = 1;

/** SL6 / N9: extended data has a size limit. 25 is a working assumption. */
export const MAX_ACTIVE_LOCATIONS = 25;
/** SL4 */
export const NAME_MAX_LENGTH = 60;

export const STATUS_ACTIVE = 'active';
export const STATUS_ARCHIVED = 'archived';
/**
 * Soft delete. The row disappears from every list — it is not in the picker and not
 * in the Archived section — but the record stays in the array. Nothing here ever
 * removes a location from storage: a listing may still carry its id, and throwing
 * the record away would leave that id pointing at nothing we can explain later.
 */
export const STATUS_DELETED = 'deleted';

export const SOURCE_GEOCODER = 'geocoder';
export const SOURCE_MANUAL = 'manual';

export const ROLE_SHIP_FROM = 'ship-from';
export const ROLE_RETURNS = 'returns';

/** Descriptive only — confirmed requirement 2. */
export const LOCATION_KINDS = ['warehouse', 'shop', 'office', 'storage-unit', 'home', 'other'];

const asArray = value => (Array.isArray(value) ? value : []);

export const getStockLocations = currentUser =>
  asArray(currentUser?.attributes?.profile?.privateData?.[STOCK_LOCATIONS_PRIVATE_KEY]);

export const getActiveLocations = locations =>
  asArray(locations).filter(
    location => location?.status !== STATUS_ARCHIVED && location?.status !== STATUS_DELETED
  );

/** The Archived section. Soft-deleted rows are not archived — they are gone from view. */
export const getArchivedLocations = locations =>
  asArray(locations).filter(location => location?.status === STATUS_ARCHIVED);

export const findLocationById = (locations, id) =>
  asArray(locations).find(location => location?.id === id) || null;

const byCreatedAtAscending = (a, b) =>
  String(a?.createdAt || '').localeCompare(String(b?.createdAt || ''));

export const resolveDefaultLocation = locations => {
  const active = getActiveLocations(locations);
  if (active.length === 0) return null;
  return active.find(location => location.isDefault === true) || active[0];
};

export const canAddLocation = locations =>
  getActiveLocations(locations).length < MAX_ACTIVE_LOCATIONS;

/**
 * SL5: exactly one active location carries isDefault: true, enforced on every
 * write rather than trusted from the caller. G6: when the default is gone, the
 * oldest remaining active location takes over; with nothing active, no default.
 */
const withSingleDefault = (locations, preferredId = null) => {
  const list = asArray(locations);
  const active = getActiveLocations(list);
  if (active.length === 0) {
    return list.map(location => ({ ...location, isDefault: false }));
  }
  const preferred = preferredId ? active.find(l => l.id === preferredId) : null;
  const current = active.find(l => l.isDefault === true);
  const oldest = active.slice().sort(byCreatedAtAscending)[0];
  const chosen = preferred || current || oldest;
  return list.map(location => ({ ...location, isDefault: location.id === chosen.id }));
};

/**
 * @returns {Object} empty when the draft is valid. Values are translation-key
 *   suffixes, resolved by the form as StockLocationForm.<suffix>.
 */
export const validateLocationDraft = draft => {
  const errors = {};
  const name = typeof draft?.name === 'string' ? draft.name.trim() : '';

  if (name.length === 0) errors.name = 'nameRequired';
  else if (name.length > NAME_MAX_LENGTH) errors.name = 'nameTooLong';

  if (!draft?.address) errors.address = 'addressRequired';

  // SL1: without a country code the shipping zone rule can never run.
  if (!draft?.countryCode) errors.countryCode = 'countryRequired';

  if (draft?.kind && !LOCATION_KINDS.includes(draft.kind)) errors.kind = 'kindInvalid';

  return errors;
};

/**
 * @param {Object} draft - the form values plus the derived geocoder parts
 * @param {Object} options
 * @param {string} options.id - crypto.randomUUID() from the caller
 * @param {string} options.now - ISO 8601 timestamp from the caller
 * @param {Object} [options.existing] - the record being edited, if any
 */
export const buildLocationRecord = (draft, { id, now, existing = null }) => {
  const geolocation = draft?.geolocation;
  const postalCode = draft?.postalCode || null;

  return {
    v: STOCK_LOCATION_SCHEMA_VERSION,
    id: existing?.id || id,
    name: String(draft?.name || '').trim(),
    kind: draft?.kind || null,
    address: draft?.address || null,
    building: draft?.building || null,
    // Plain numbers: this record is stored as JSON in profile privateData.
    geolocation: geolocation ? { lat: geolocation.lat, lng: geolocation.lng } : null,
    countryCode: String(draft?.countryCode || '').toUpperCase() || null,
    region: draft?.region || null,
    city: draft?.city || null,
    postalCode,
    precision: draft?.precision || precisionFor(postalCode),
    contactName: draft?.contactName || null,
    phone: draft?.phone || null,
    isDefault: !!draft?.isDefault,
    roles: asArray(draft?.roles).length > 0 ? draft.roles : [ROLE_SHIP_FROM],
    status: existing?.status || STATUS_ACTIVE,
    source: draft?.source || SOURCE_GEOCODER,
    createdAt: existing?.createdAt || now,
    updatedAt: now,
  };
};

export const upsertLocation = (locations, record) => {
  const list = asArray(locations);
  const exists = list.some(location => location.id === record.id);
  const next = exists
    ? list.map(location => (location.id === record.id ? record : location))
    : [...list, record];

  // G2: a default among one is not a choice — the first location just becomes it.
  const isOnlyActive = getActiveLocations(next).length === 1;
  const preferredId = record.isDefault || isOnlyActive ? record.id : null;

  return withSingleDefault(next, preferredId);
};

export const setDefaultLocation = (locations, id) => withSingleDefault(asArray(locations), id);

const setStatus = (locations, id, status, now) =>
  asArray(locations).map(location =>
    location.id === id ? { ...location, status, updatedAt: now || location.updatedAt } : location
  );

export const archiveLocation = (locations, id, now = null) =>
  withSingleDefault(setStatus(locations, id, STATUS_ARCHIVED, now));

export const restoreLocation = (locations, id, now = null) =>
  withSingleDefault(setStatus(locations, id, STATUS_ACTIVE, now));

/**
 * Always allowed (D3, Q7). Listings hold their own copy, so nothing breaks.
 *
 * A SOFT delete: the record keeps its place in the array with status 'deleted' and
 * simply stops being shown anywhere. It is never spliced out. A listing can still
 * carry this id (AC25), and an id that resolves to a deleted record is a question we
 * can answer; an id that resolves to nothing is not.
 */
export const deleteLocation = (locations, id, now = null) =>
  withSingleDefault(setStatus(locations, id, STATUS_DELETED, now));

/**
 * The single write path for everything a listing stores about its location
 * (SL7). One shape for every listing, whatever the delivery options — that is
 * the code-level meaning of D7.
 *
 * @param {Object} location - a saved location record
 * @param {Object} existingPublicData - the listing's current publicData
 * @param {Object} options
 * @param {string} options.now - ISO 8601 timestamp from the caller
 */
export const buildListingLocationPatch = (location, existingPublicData, { now }) => {
  const geolocation = location?.geolocation;
  const existingShippingConfig = existingPublicData?.[SHIPPING_CONFIG_KEY] || null;

  // Same builder the delivery step uses, so the two writers of this key cannot drift.
  const publicOrigin = buildPublicOrigin(location, now);

  return {
    geolocation: geolocation ? new LatLng(geolocation.lat, geolocation.lng) : null,
    publicData: {
      [STOCK_LOCATION_ID_PUBLIC_KEY]: location.id,
      // The template's own location keys: this is what puts the listing on the
      // map and into Sharetribe's origin-and-bounds location search (D6).
      location: { address: location.address, building: location.building || '' },
      // N4: a publicData value is REPLACED, not merged. Spread the stored config
      // or this write would delete the shipping prices.
      ...(existingShippingConfig
        ? { [SHIPPING_CONFIG_KEY]: { ...existingShippingConfig, origin: publicOrigin } }
        : {}),
    },
    privateData: {
      // Structure, not secrecy: the public copy is one formatted string, and a
      // carrier API needs separate fields (C3, N5). contactName and phone are
      // here and never in publicData (SL9).
      [SHIPPING_ORIGIN_PRIVATE_KEY]: {
        stockLocationId: location.id,
        // The seller's own name for the place ("Our Secondary Shop"). Copied like
        // everything else here (D3), so renaming a saved location does not rename it
        // on listings already saved. Without it the delivery step's summary had to
        // use the full formatted address and read "…Sparta, Peloponnese, Greece,
        // Sparta" — the city twice.
        name: location.name || null,
        address: location.address,
        building: location.building || null,
        city: location.city || null,
        postalCode: location.postalCode || null,
        region: location.region || null,
        countryCode: location.countryCode,
        geolocation: geolocation ? { lat: geolocation.lat, lng: geolocation.lng } : null,
        precision: location.precision || null,
        contactName: location.contactName || null,
        phone: location.phone || null,
        capturedAt: now,
      },
    },
  };
};

export const getListingStockLocationId = listing =>
  listing?.attributes?.publicData?.[STOCK_LOCATION_ID_PUBLIC_KEY] ||
  listing?.attributes?.privateData?.[SHIPPING_ORIGIN_PRIVATE_KEY]?.stockLocationId ||
  null;

/**
 * The origin the delivery step and the publish gate should use: the listing's
 * own copy, never a lookup of the seller's address book (J1, D3). Falls back to
 * the public origin so listings saved before this feature still work (AC23).
 */
export const getListingOrigin = listing => {
  const privateOrigin = listing?.attributes?.privateData?.[SHIPPING_ORIGIN_PRIVATE_KEY] || null;
  const publicOrigin = listing?.attributes?.publicData?.[SHIPPING_CONFIG_KEY]?.origin || null;
  const countryCode = privateOrigin?.countryCode || publicOrigin?.countryCode || null;

  if (!countryCode) return null;

  return {
    stockLocationId: privateOrigin?.stockLocationId || publicOrigin?.stockLocationId || null,
    countryCode,
    region: privateOrigin?.region || publicOrigin?.region || null,
  };
};
